/**
 * Layer 2 of demo security stack — full-screen tiled diagonal watermark
 * overlay rendered ONLY when user.is_demo === true.
 *
 * Watermark text: CONFIDENTIAL · {fund_name} · {investor_name} · Session
 * {session_id} · {timestamp} · TRACKED
 *
 * The overlay is pointer-events:none so it does not interfere with admin-page
 * UX, but is rendered ABOVE all admin content (z-150) so any screenshot
 * captures the viewer attribution. Live timestamp ticks every minute so each
 * frame can be uniquely traced.
 *
 * Layer 7 (Hidden Trap Markers) is bundled here: invisible data-attrs are
 * rendered carrying session id, viewer hash, and a per-mount nonce — so any
 * forwarded HTML can be traced back to the original viewer even if the
 * watermark is masked client-side.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "")}/api`;

function fmtNow() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())} UTC${d.getTimezoneOffset() >= 0 ? "-" : "+"}${pad(Math.abs(d.getTimezoneOffset() / 60))}`;
}

export default function DemoWatermark() {
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [now, setNow] = useState(fmtNow());

  useEffect(() => {
    if (user?.is_demo !== true) return;
    let alive = true;
    axios.get(`${API}/public/demo-session`)
      .then((r) => { if (alive && r.data?.is_demo) setSession(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [user]);

  // Live timestamp tick every 60s so screenshots are uniquely traceable.
  useEffect(() => {
    if (user?.is_demo !== true) return undefined;
    const t = setInterval(() => setNow(fmtNow()), 60000);
    return () => clearInterval(t);
  }, [user]);

  // Per-mount nonce — used as a hidden trap marker.
  const nonce = useMemo(() => Math.random().toString(36).slice(2, 10), []);

  if (user?.is_demo !== true) return null;

  const investor = session?.investor_name || "(viewer)";
  const fund = session?.fund_name || "(fund)";
  const sid = session?.session_id || "DGX-XXXXX";
  const text = `CONFIDENTIAL · ${fund} · ${investor} · Session ${sid} · ${now} · TRACKED`;

  // Generate a 6×8 grid of overlapping watermark instances.
  const tiles = [];
  for (let r = 0; r < 8; r++) {
    for (let c = 0; c < 6; c++) {
      tiles.push({
        key: `${r}-${c}`,
        top: `${r * 14 + (c % 2 ? 6 : 0)}%`,
        left: `${c * 18 - 6}%`,
      });
    }
  }

  return (
    <>
      {/* Visible diagonal tiled watermark — pointer-events:none */}
      <div
        className="fixed inset-0 z-[150] pointer-events-none select-none overflow-hidden"
        aria-hidden="true"
        data-testid="demo-watermark"
        data-dgx-sid={sid}
        data-dgx-via={session?.via || ""}
        data-dgx-nonce={nonce}
      >
        {tiles.map((t) => (
          <div
            key={t.key}
            style={{
              position: "absolute",
              top: t.top,
              left: t.left,
              transform: "rotate(-28deg)",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              fontSize: "10px",
              color: "rgba(34, 197, 94, 0.10)",
              fontWeight: 600,
              letterSpacing: "0.05em",
              whiteSpace: "nowrap",
              textTransform: "uppercase",
              userSelect: "none",
            }}
          >
            {text}
          </div>
        ))}
      </div>

      {/* Bottom-right floating chip — high-contrast, always visible.
          Pointer-events: auto so the user can click "Read the notice". */}
      <div
        className="fixed bottom-3 right-3 z-[151] bg-[#22C55E]/15 border border-[#22C55E]/40 backdrop-blur-md px-3 py-2 text-[10px] font-mono text-[#22C55E] shadow-lg"
        data-testid="demo-watermark-chip"
      >
        <p className="font-bold tracking-wider">DELTA GREENX · CONFIDENTIAL</p>
        <p className="text-[#22C55E]/80 truncate max-w-[260px]">{fund} · {investor}</p>
        <p className="text-[#22C55E]/60">Session {sid} · {now}</p>
      </div>

      {/* Layer 7 — invisible audit markers. Embedded session id / via /
          nonce in DOM attributes for forensic tracing of leaked HTML. */}
      <span
        aria-hidden="true"
        style={{ position: "absolute", width: 0, height: 0, overflow: "hidden", color: "transparent" }}
        data-dgx-marker="audit"
        data-dgx-sid={sid}
        data-dgx-via={session?.via || ""}
        data-dgx-fund={fund}
        data-dgx-nonce={nonce}
      >
        {`__dgx_${sid}_${nonce}__`}
      </span>
    </>
  );
}
